import {
  DataRoomFolderBrowseFragmentFragment,
  DataRoomPermission,
  DealRoleType,
  PermissionsResourceType,
} from "@/src/graphql/generated";
import clsx from "clsx";
import { ReactNode, useContext, useEffect } from "react";
import { FolderContext } from "../folder_view";
import { Checkbox } from "@/src/components/checkbox";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { TableCell } from "./TableCell";
import { TableText } from "./TableText";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { FolderIcon } from "@heroicons/react/24/solid";
import { Menu } from "@headlessui/react";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import { FileIcon } from "@/src/components/FileIcon";
import { DataRoomContext } from "..";
import { getDataRoomIndex } from "@/src/utils/getDataRoomIndex";
import { truncateText } from "@/src/utils/truncateText";
import { DdqlPill } from "../DdqlPill";
import { QuestionPill } from "../QuestionPill";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { usePermissionCheck } from "@/src/hooks/usePermissionCheck";
import { DataRoomPermissions } from "../../access/AccessList";
import { useDataRoomItemDrag } from "../dnd/useDataRoomItemDrag";
import { useFolderDropTarget } from "../dnd/useFolderDropTarget";
import { DTRM_ID_ATTR } from "../dnd/collapseIntoDrag";
import { usePrefetchDataRoomFolderBrowse } from "@/src/hooks/usePrefetchDataRoomFolderBrowse";
import { usePrefetchDataRoomFileShell } from "@/src/hooks/usePrefetchDataRoomFileShell";

type BrowseFolder = DataRoomFolderBrowseFragmentFragment["folders"][number];
type BrowseFile = DataRoomFolderBrowseFragmentFragment["files"][number];

interface DataRoomTableRowProps {
  type: "folder" | "file";
  folder?: BrowseFolder;
  file?: BrowseFile;
  index: number;
  ddql?: { id: string; name: string } | null;
  question?: { id: string; title: string } | null;
}

export function DataRoomTableRow(props: DataRoomTableRowProps) {
  const { isDragging, onItemsDropped, selectItem } = useContext(FolderContext);

  const { selectedFolders, selectedFiles } = useContext(DataRoomContext);

  const activeDealId = useParams<{ dealId: string }>();
  const hasEditAccess = usePermissionCheck({ permissionKey: "documents:edit" });

  if (props.type === "folder") {
    if (!props.folder) {
      return null;
    }

    const isPartOfDraggingGroup =
      isDragging && selectedFolders.includes(props.folder.id);

    return (
      <TableRow
        {...props}
        itemId={props.folder.id}
        itemType="folder"
        isDraggingEnabled={hasEditAccess !== false}
        isDropEnabled={hasEditAccess !== false}
        isPartOfDraggingGroup={isPartOfDraggingGroup}
        isChecked={selectedFolders.includes(props.folder.id)}
        onSelect={(shiftKey) => {
          selectItem(props.folder!.id, "folder", { shiftKey });
        }}
        selectedFiles={selectedFiles}
        selectedFolders={selectedFolders}
        onDrop={onItemsDropped}
        icon={
          <FolderIcon className="w-5 h-5 text-blue-700/70 flex-shrink-0" />
        }
        name={props.folder.name}
        createdBy={props.folder.createdBy.name}
        updatedAt={props.folder.updatedAt}
        link={`/deal/${activeDealId.dealId}/documents/folder/${props.folder.id}`}
        actions={<FolderActions folder={props.folder} />}
        dataRoomIndex={getDataRoomIndex(
          props.folder.index?.sortIndex,
          props.folder.parentFolders,
          "folder",
        )}
        ddql={props.ddql}
        question={props.question}
        permissions={props.folder.permissions}
        hasFolderWriteAccess={props.folder.hasFolderWriteAccess}
      />
    );
  }

  if (props.type === "file") {
    if (!props.file) {
      return null;
    }

    const file = props.file;
    const isPartOfDraggingGroup =
      isDragging && selectedFiles.includes(file.id);

    return (
      <TableRow
        {...props}
        itemId={file.id}
        itemType="file"
        isDraggingEnabled={hasEditAccess !== false}
        isDropEnabled={false}
        isPartOfDraggingGroup={isPartOfDraggingGroup}
        isChecked={selectedFiles.includes(file.id)}
        onSelect={(shiftKey) => {
          selectItem(file.id, "file", { shiftKey });
        }}
        selectedFiles={selectedFiles}
        selectedFolders={selectedFolders}
        onDrop={onItemsDropped}
        icon={<FileIcon fileType={file.fileType} />}
        name={file.name}
        createdBy={file.createdBy.name}
        updatedAt={file.updatedAt}
        permissions={file.permissions}
        link={`/deal/${activeDealId.dealId}/documents/folder/${file.folderId}/file/${file.id}`}
        actions={<FileActions file={file} />}
        dataRoomIndex={getDataRoomIndex(
          file.index?.sortIndex,
          file.parentFolders,
          "file",
        )}
      />
    );
  }

  return null;
}

type DataRoomTableRowInnerProps = DataRoomTableRowProps & {
  itemType: "file" | "folder";
  isDraggingEnabled: boolean;
  isDropEnabled: boolean;
  isPartOfDraggingGroup: boolean;
  selectedFiles: string[];
  selectedFolders: string[];
  onDrop: (args: {
    targetFolderId: string;
    targetFolderName: string;
    fileIds: string[];
    folderIds: string[];
  }) => void;

  isChecked: boolean;
  onSelect: (shiftKey: boolean) => void;

  itemId: string;
  dataRoomIndex: string;
  icon: ReactNode;
  name: string;
  createdBy: string;
  updatedAt: number;
  permissions?: Pick<DataRoomPermission, "id" | "name" | "accessLevel">[];
  link: string;

  actions?: ReactNode;
  ddql?: { id: string; name: string } | null;
  question?: { id: string; title: string } | null;
  hasFolderWriteAccess?: boolean;
};

function TableRow(props: DataRoomTableRowInnerProps) {
  const { ddql, question, isPartOfDraggingGroup } = props;
  const history = useHistory();

  const {
    selectedFolders,
    selectedFiles,
    setSelectedFolders,
    setSelectedFiles,
    indexingEnabled,
  } = useContext(DataRoomContext);

  const { registerExternalFolderTarget } = useContext(FolderContext);

  const prefetchFolderHandlers = usePrefetchDataRoomFolderBrowse(
    props.itemType === "folder" ? props.itemId : "",
  );
  const prefetchFileHandlers = usePrefetchDataRoomFileShell(
    props.itemType === "file" ? props.itemId : "",
  );
  const prefetchHandlers =
    props.itemType === "folder" ? prefetchFolderHandlers : prefetchFileHandlers;

  const rowRef = useDataRoomItemDrag({
    itemType: props.itemType,
    itemId: props.itemId,
    itemName: props.name,
    enabled: props.isDraggingEnabled,
    selectedFiles: props.selectedFiles,
    selectedFolders: props.selectedFolders,
  });

  useFolderDropTarget({
    folderId: props.itemId,
    folderName: props.name,
    enabled: props.isDropEnabled && props.itemType === "folder",
    rowRef,
    selectedFolders: props.selectedFolders,
    onDrop: props.onDrop,
  });

  const permissionId = props.permissions?.[0]?.id;
  const permissionName = props.permissions?.[0]?.name;

  useEffect(() => {
    if (props.itemType !== "folder") return;
    if (!props.hasFolderWriteAccess) return;
    const permission = permissionId
      ? {
          id: permissionId,
          name: permissionName ?? "",
          description: undefined,
        }
      : null;
    registerExternalFolderTarget(
      {
        folderId: props.itemId,
        folderName: props.name,
        permission,
      },
      rowRef.current,
    );
    return () => {
      registerExternalFolderTarget(
        { folderId: props.itemId, folderName: props.name, permission: null },
        null,
      );
    };
  }, [
    props.itemType,
    props.itemId,
    props.name,
    props.hasFolderWriteAccess,
    permissionId,
    permissionName,
    registerExternalFolderTarget,
  ]);

  return (
    <tr
      ref={rowRef}
      {...{ [DTRM_ID_ATTR]: props.itemId }}
      className={clsx(
        "cursor-pointer w-full table-row group data-[drop-over=true]:bg-persian-100 data-[external-drop-over=true]:bg-persian-100",
        isPartOfDraggingGroup ? "opacity-70" : "",
        props.isChecked ? "bg-persian-100/50" : "hover:bg-gray-50",
      )}
      onMouseEnter={prefetchHandlers.onMouseEnter}
      onMouseLeave={prefetchHandlers.onMouseLeave}
      onFocus={prefetchHandlers.onFocus}
      onClick={(e) => {
        if (e.defaultPrevented) {
          return;
        }

        if (e.shiftKey || selectedFiles.length + selectedFolders.length >= 1) {
          props.onSelect(e.shiftKey);
          return;
        }

        setSelectedFiles([]);
        setSelectedFolders([]);
        history.push(props.link);
      }}
    >
      <TableCell
        stopPropagation={true}
        className="hidden md:table-cell"
        onClick={(e) => {
          props.onSelect(e.shiftKey);
        }}
      >
        <Checkbox
          id={props.itemId}
          checked={props.isChecked}
          onClick={(e) => {
            // Handle here so shiftKey is reliable (change events are not).
            props.onSelect(e.shiftKey);
          }}
          onChange={() => {
            // Controlled by selection state; click handler performs the update.
          }}
        />
      </TableCell>
      {indexingEnabled ? (
        <TableCell className="align-middle">
          <div className="flex items-center justify-start h-full">
            <p className="text-sm text-concrete-950">{props.dataRoomIndex}</p>
          </div>
        </TableCell>
      ) : null}
      <TableCell className="w-full">
        <div className="flex items-center gap-x-2 relative">
          {props.icon}
          <p
            className="text-concrete-950 font-semibold text-sm"
            title={props.name}
          >
            {truncateText(props.name, 70)}
          </p>
          {ddql ? <DdqlPill ddql={ddql} /> : null}
          {question ? <QuestionPill question={question} /> : null}
        </div>
      </TableCell>
      <PermissionCheck
        input={{
          permissionKey: "documents:has_access",
          roleType: DealRoleType.Internal,
        }}
      >
        <TableCell visibilityClass="hidden xl:table-cell" className="truncate">
          <TableText title={props.createdBy}>
            {props.createdBy.length > 20
              ? `${props.createdBy.slice(0, 20)}...`
              : props.createdBy}
          </TableText>
        </TableCell>
      </PermissionCheck>
      <TableCell visibilityClass="hidden md:table-cell">
        <TableText>
          {formatDistanceToNowStrict(fromUnixTime(props.updatedAt), {
            addSuffix: true,
          })}
        </TableText>
      </TableCell>
      <PermissionCheck
        input={{
          permissionKey: "documents:has_access",
          roleType: DealRoleType.Internal,
        }}
      >
        <TableCell visibilityClass="hidden lg:table-cell">
          {props.permissions ? (
            <DataRoomPermissions
              prefixText=""
              restrictedAccess={false}
              granularAccess={{ files: [], folders: [] }}
              permissions={props.permissions}
            />
          ) : null}
        </TableCell>
      </PermissionCheck>
      <PermissionCheck
        input={{
          permissionKey: "documents:edit",
          resource: {
            resourceID: props.itemId,
            resourceType: props.file
              ? PermissionsResourceType.Document
              : PermissionsResourceType.Folder,
          },
        }}
      >
        <TableCell visibilityClass="hidden sm:table-cell">
          {props.actions}
        </TableCell>
      </PermissionCheck>
    </tr>
  );
}

function FolderActions(props: { folder: BrowseFolder }) {
  const history = useHistory();
  const location = useLocation();

  const { setEditFolderId, setMoveFolderId } = useContext(FolderContext);

  return (
    <TripleDotMenu mode="table">
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setMoveFolderId(props.folder.id);
            }}
            className={clsx(
              active ? "bg-gray-50 text-blue-700" : "",
              "block px-3 py-1 text-sm leading-6 text-blue-600 cursor-pointer",
            )}
          >
            Move
          </div>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setEditFolderId(props.folder.id);
            }}
            className={clsx(
              active ? "bg-gray-50 text-blue-700" : "",
              "block px-3 py-1 text-sm leading-6 text-blue-600 cursor-pointer",
            )}
          >
            Edit
          </div>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              const params = new URLSearchParams({
                deleteFolder: props.folder.id,
              });
              history.replace({
                pathname: location.pathname,
                search: params.toString(),
              });
            }}
            className={clsx(
              active ? "bg-gray-50 text-red-700" : "",
              "block px-3 py-1 text-sm leading-6 text-red-600 cursor-pointer",
            )}
          >
            Delete
          </div>
        )}
      </Menu.Item>
    </TripleDotMenu>
  );
}

function FileActions(props: { file: BrowseFile }) {
  const { setEditFileId, setMoveFileId, setDeleteFileId } =
    useContext(FolderContext);
  return (
    <TripleDotMenu mode="table">
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setEditFileId(props.file.id);
            }}
            className={clsx(
              active ? "bg-gray-50 text-blue-700" : "",
              "block px-3 py-1 text-sm leading-6 text-blue-600 cursor-pointer",
            )}
          >
            Edit
          </div>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setMoveFileId(props.file.id);
            }}
            className={clsx(
              active ? "bg-gray-50 text-blue-700" : "",
              "block px-3 py-1 text-sm leading-6 text-blue-600 cursor-pointer",
            )}
          >
            Move
          </div>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setDeleteFileId(props.file.id);
            }}
            className={clsx(
              active ? "bg-gray-50 text-red-700" : "",
              "block px-3 py-1 text-sm leading-6 text-red-600 cursor-pointer",
            )}
          >
            Delete
          </div>
        )}
      </Menu.Item>
    </TripleDotMenu>
  );
}
